/**
 * Panneau de liaison d'un step de leçon à un step d'exercice (correction ou
 * distracteur). L'admin choisit un exercice V2 du chapitre, puis un bloc cible,
 * puis le type de lien.
 */

import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { MarkdownMath } from '@jeraisonne/ui';
import { useExerciseV2 } from '@/lib/exercise-v2/api';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { useChapterV2Exercises, useLessonBlockLinkMutate } from '@/lib/lesson-v2/api';
import type { LessonBlockLinkType } from '@/lib/lesson-v2/types';
import { ExercisePickerField } from '@/components/pickers/exercise-picker';

export interface LinkPickerProps {
  lessonId: string;
  chapterId: string;
  lessonBlockId: string;
  onClose: () => void;
}

export function LinkPicker({ lessonId, chapterId, lessonBlockId, onClose }: LinkPickerProps) {
  const { data: exercises = [], isPending: exoListPending } = useChapterV2Exercises(chapterId);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(null);
  const [linkType, setLinkType] = useState<LessonBlockLinkType>('teaches');
  const exoV2 = useExerciseV2(selectedExerciseId);
  const linkMutate = useLessonBlockLinkMutate(lessonId);

  const targets = useMemo<ExerciseBlock[]>(() => {
    const blocks = exoV2.data?.blocks ?? [];
    return blocks
      .filter((b) => b.block_type === 'correction' || b.block_type === 'distractor')
      .sort((a, b) => a.position - b.position);
  }, [exoV2.data]);

  function preview(b: ExerciseBlock): string {
    const d = b.data as Record<string, string | undefined>;
    if (b.block_type === 'correction') return d.answer_md || d.explanation_md || '(corrigé)';
    return d.label_md || '(distracteur)';
  }

  async function handleLink(exerciseBlockId: string) {
    await linkMutate.mutateAsync({
      op: 'link',
      lesson_block_id: lessonBlockId,
      exercise_block_id: exerciseBlockId,
      link_type: linkType,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Lier ce step à un exercice</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 min-w-9 rounded p-1 text-slate-500 hover:bg-slate-100"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {/* Type de lien */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-600">Type de lien</label>
            <div className="flex flex-wrap gap-1">
              {(
                [
                  ['teaches', 'Enseigne (corrigé)'],
                  ['misconception', 'Corrige (distracteur)'],
                  ['reinforces', 'Renforce'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setLinkType(value)}
                  className={`min-h-9 rounded-full border px-3 py-1 text-xs font-medium ${
                    linkType === value
                      ? 'border-slate-800 bg-slate-800 text-white'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Sélecteur d'exercice */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-600">
              Exercice V2 du chapitre
            </label>
            {exoListPending ? (
              <p className="text-xs text-slate-400">Chargement…</p>
            ) : exercises.length === 0 ? (
              <p className="text-xs text-slate-400">
                Aucun exercice V2 dans ce chapitre. Migrez un exercice en V2 d'abord.
              </p>
            ) : (
              <ExercisePickerField
                items={exercises.map((ex) => ({ id: ex.id, title: ex.title }))}
                value={selectedExerciseId}
                onChange={(id) => setSelectedExerciseId(id)}
                title="Choisir l’exercice à lier"
              />
            )}
          </div>

          {/* Blocs cibles */}
          {selectedExerciseId ? (
            exoV2.isPending ? (
              <p className="text-xs text-slate-400">Chargement des steps…</p>
            ) : targets.length === 0 ? (
              <p className="text-xs text-slate-400">
                Cet exercice n'a pas encore de corrigé ni de distracteur.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {targets.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => handleLink(b.id)}
                      disabled={linkMutate.isPending}
                      className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 disabled:opacity-50 ${
                        b.block_type === 'distractor' ? 'border-amber-200' : 'border-emerald-200'
                      }`}
                    >
                      <span
                        className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                          b.block_type === 'distractor'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {b.block_type === 'distractor' ? 'Distracteur' : 'Corrigé'}
                      </span>
                      <MarkdownMath className="min-w-0 flex-1 text-xs text-slate-700">
                        {preview(b)}
                      </MarkdownMath>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}

          {linkMutate.isError ? (
            <p className="text-xs text-red-600">{(linkMutate.error as Error).message}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
